import type {
  CalendarAffected,
  CalendarEvent,
  CalendarEventInput,
} from '@/features/calendar/api';

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/**
 * RL-1046: what saving an entry did to the bookings in its period. Uses the
 * create response's breakdown (`affected`) when there is one, else the stored
 * `affectedCount` (which also counts "any doctor" and checked-in bookings).
 * Zero means no booking is affected, not that the period has no bookings.
 */
export function affectedText(
  count: number,
  affected?: CalendarAffected,
): string {
  if (!affected)
    return count > 0
      ? `${plural(count, 'booking', 'bookings')} in this period affected.`
      : 'No booking in this period is affected.';
  if (affected.total <= 0) return 'No booking in this period is affected.';
  return [
    affected.askedToRebook > 0 &&
      `${plural(affected.askedToRebook, 'patient was', 'patients were')} asked to pick a new time.`,
    affected.anyDoctor > 0 &&
      `${plural(affected.anyDoctor, '"any doctor" booking falls', '"any doctor" bookings fall')} on these days: check another doctor can see ${affected.anyDoctor === 1 ? 'the patient' : 'them'}.`,
    affected.checkedIn > 0 &&
      `${plural(affected.checkedIn, 'patient has', 'patients have')} already checked in.`,
  ]
    .filter(Boolean)
    .join(' ');
}

/** "08:00 to 13:00" (the API's shortHours) → opens/closes. */
export function parseShortHours(
  shortHours: string | null,
): { opens: string; closes: string } | null {
  const m = shortHours?.match(/^(\d{2}:\d{2}) to (\d{2}:\d{2})$/);
  return m ? { opens: m[1], closes: m[2] } : null;
}

/**
 * RL-1047: the create body that adds the same entry again (Undo after a
 * remove; there is no edit API, so a change is remove + add).
 */
export function recreateInput(e: CalendarEvent): CalendarEventInput {
  const hours = e.type === 'short_hours' ? parseShortHours(e.shortHours) : null;
  return {
    type: e.type,
    from: e.from,
    to: e.to,
    branchId: e.branch?.id ?? null,
    ...(e.type === 'doctor_leave' && e.doctor
      ? { doctorStaffId: e.doctor.id }
      : {}),
    description: e.description,
    ...(hours ?? {}),
  };
}

/** "7 Oct 2026" or "7 – 8 Oct 2026" for an entry's dates. */
export function eventDates(e: Pick<CalendarEvent, 'from' | 'to'>): string {
  const fmt = (d: string) =>
    new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  return e.from === e.to ? fmt(e.from) : `${fmt(e.from)} – ${fmt(e.to)}`;
}
